import { css, cx } from '@emotion/css'
import React from 'react'
import { FormattedMessage, FormattedDate } from 'react-intl'
import { theme } from '@contrast-security-inc/design-system-foundations'
import { LoadingMaskRectangle } from '../../legacy-design-system/loading-masks'
import { tooltipBodyCss } from '../../legacy-design-system/text'
import { tooltipTitleCss, WithTooltip } from '../../design-system'
import {
  SegmentedThermometer,
  ThermometerValueRendererProps
} from '../../legacy-design-system/thermometer'
import { Link } from '@contrast-security-inc/design-system-typography'

interface LicenseExpiration {
  licenses: number
  expirationDateTime: string
}

export interface LicenseThermometerProps {
  used: number
  available: number
  expirationDateUsedLicenses: LicenseExpiration[]
  expirationDateAvailableLicenses: LicenseExpiration[]
  testId?: string
  redirectLink?: string
}

const isUsedLicenses = (index) => index === 0

export function LicenseThermometerLoadingView() {
  return (
    <LoadingMaskRectangle
      width={400}
      height={24}
      className={css`
        width: 100%;
      `}
    />
  )
}

export function LicenseThermometer({
  used,
  available,
  expirationDateUsedLicenses,
  expirationDateAvailableLicenses,
  testId,
  redirectLink
}: LicenseThermometerProps) {
  const values = [used, available]

  return (
    <SegmentedThermometer
      testId={testId}
      values={values}
      getColor={(i) => (isUsedLicenses(i) ? 'teal50' : 'neutral40')}
      renderValue={({ index, width, propsGetter }) => {
        const isUsedLicensesSegment = isUsedLicenses(index)

        return values[index] > 0 ? (
          <LicenseThermometerSegment
            isUsedLicensesSegment={isUsedLicensesSegment}
            expirationDateUsedLicenses={expirationDateUsedLicenses}
            expirationDateAvailableLicenses={expirationDateAvailableLicenses}
            color={
              isUsedLicensesSegment ? theme.color.white : theme.color.neutral100
            }
            data-e2e={usedAndUnusedTestIDs(isUsedLicensesSegment, testId)}
            width={width}
            redirectLink={redirectLink}
            {...propsGetter()}
          />
        ) : null
      }}
    />
  )
}

function usedAndUnusedTestIDs(isUsedLicensesSegment, testId) {
  return isUsedLicensesSegment ? 'used-' + testId : 'unused-' + testId
}

/*
 * This width calculation effectively caps the ratio between two segments of the thermometer.
 * This allows us to display two segments that differ greatly in width while still allowing
 * enough width for the contents of the smaller segment.
 */
export const getSegmentWidth = (width: string) => {
  const parsedWidth: number = parseFloat(width)

  // If there are two segments in the thermometer, the smallest width of one segment is 10%
  if (parsedWidth < 10) {
    return '10%'
  }
  // If there are two segments in the thermometer, the largest width of one segment is 90%
  if (parsedWidth > 90 && parsedWidth < 100) {
    return '90%'
  }

  return width
}

function LicenseThermometerSegment({
  color,
  width,
  className,
  expirationDateUsedLicenses,
  expirationDateAvailableLicenses,
  isUsedLicensesSegment,
  redirectLink,
  ...renderValueProps
}: ThermometerValueRendererProps & {
  color: string
  expirationDateUsedLicenses: LicenseExpiration[]
  expirationDateAvailableLicenses: LicenseExpiration[]
  isUsedLicensesSegment: boolean
  redirectLink?: string
}) {
  return (
    <WithTooltip
      width={width}
      placement="top"
      positionFixed
      renderTooltipContent={() => (
        <LicenseThermometerSegmentTooltipContent
          titleId={
            isUsedLicensesSegment
              ? 'title_usedLicenses'
              : 'title_availableLicenses'
          }
          expirationDateLicenses={
            isUsedLicensesSegment
              ? expirationDateUsedLicenses
              : expirationDateAvailableLicenses
          }
        />
      )}
    >
      {({
        isOpen: _isOpen,
        isTransitioning: _isTransitioning,
        ...withTooltipChildProps
      }) => {
        return (
          <div
            {...withTooltipChildProps}
            className={cx(
              className,
              css`
                color: ${color};
                height: 24px;
                width: 100%;
              `
            )}
            style={{ width: getSegmentWidth(width) }}
            {...renderValueProps}
          >
            {redirectLink && (
              <Link
                style={{ height: '24px', width: '100%' }}
                href={redirectLink}
              />
            )}
          </div>
        )
      }}
    </WithTooltip>
  )
}

interface LicenseThermometerSegmentTooltipContentProps {
  titleId: string
  expirationDateLicenses: LicenseExpiration[]
}

function LicenseThermometerSegmentTooltipContent({
  titleId,
  expirationDateLicenses
}: LicenseThermometerSegmentTooltipContentProps) {
  return (
    <div>
      <FormattedMessage
        id={titleId}
        children={(title) => <div className={tooltipTitleCss}>{title}</div>}
      />
      {expirationDateLicenses.map(({ licenses, expirationDateTime }, key) => {
        return (
          <FormattedMessage
            key={key}
            id="detail_licenses_expireDate"
            children={(expireMessage) => (
              <FormattedDate
                value={expirationDateTime}
                children={(dateText) => (
                  <div
                    className={tooltipBodyCss}
                  >{`${licenses} ${expireMessage} ${dateText}`}</div>
                )}
              />
            )}
          />
        )
      })}
    </div>
  )
}
